import type { AddressInfo } from "features/dt-basic-setup/single-building/model/types";

import { uniqueNonEmpty } from "utils/dataManipulation";

import type { ReverseGeocodedAddress } from "../types";

const extractStreetAndNumber = (address: Record<string, string | undefined>) => {
  const road = address.road || address.pedestrian || address.footway || "";
  const houseNumber = address.house_number || "";
  return [road, houseNumber].filter(Boolean).join(" ").trim();
};

export const createAddressQuery = (addressInfo: AddressInfo | undefined) =>
  [addressInfo?.address, addressInfo?.postal_code, addressInfo?.city, addressInfo?.country]
    .filter(Boolean)
    .join(", ")
    .trim();

const joinAddressParts = (...parts: (string | undefined)[]) => parts.filter(Boolean).join(", ").trim();

export const simplifyAddressLine = (addressLine: string) =>
  addressLine
    .replace(/(\d+[a-zA-Z]?(?:\s*[-/]\s*\d+[a-zA-Z]?)?)(?:\s*[,;]\s*\d+[a-zA-Z]?(?:\s*[-/]\s*\d+[a-zA-Z]?)?)+$/, "$1")
    .trim();

export const buildGeocodingQueryCandidates = (addressInfo?: Partial<AddressInfo>) => {
  const { address, city, country, postal_code } = addressInfo ?? {};
  const simplifiedAddress = address ? simplifyAddressLine(address) : undefined;

  return uniqueNonEmpty([
    joinAddressParts(address, postal_code, city, country),
    joinAddressParts(simplifiedAddress, postal_code, city, country),
  ]);
};

export const buildAddressInfoFromGeocoded = (address: ReverseGeocodedAddress): AddressInfo => {
  const newAddress = extractStreetAndNumber(address);
  const newCity = address.city || address.town || address.village || address.municipality;
  const newPostalCode = address.postcode;
  const newCountry = address.country;

  const newAddressInfo: AddressInfo = {};

  if (newAddress) newAddressInfo.address = newAddress;
  if (newCity) newAddressInfo.city = newCity;
  if (newPostalCode) newAddressInfo.postal_code = newPostalCode;
  if (newCountry) newAddressInfo.country = newCountry;

  return newAddressInfo;
};
